import React, {
    useEffect, useState,
} from 'react'
import axios from 'axios'
import { ImageResized } from '../../../components/shared/webp-image-resized'
import classnames from 'classnames'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useMutation } from 'react-query'
import Button from '../shared/sn-button'
import SnIcon from '../shared/sn-icon'
import MessengerButton from '../users/parts/messenger-button'
import { FRIENDSHIP_STATE_ACCEPTED } from '../../../consts'
import { renderFriendshipButtons } from '../users/parts/renders/renderFriendshipButtons'
import Link from '../shared/sn-link'

const StyledUserBox = styled.div`
    &::after {
        content: none !important;
    }
    min-width: 200px;
`

const StyledUserAvatar = styled(ImageResized)`
  width: 100px;
  height: 100px;
`

const cheerUser = userIdToCheer =>
    axios.post(`${window.APP_CONFIG.REACT_APP_API_URL}api/cheers`,
        {
            id: userIdToCheer,
            type: 'users',
        })
        .then(response => response.data)

const uncheerUser = userIdToUncheer =>
    axios.delete(`${window.APP_CONFIG.REACT_APP_API_URL}api/cheers?id=${userIdToUncheer}&type=users`)
        .then(response => response.data)

const toggleUserCheering = ({
    userId,
    isCheering,
}) => isCheering ? cheerUser(userId) : uncheerUser(userId)

const useToggleUserCheer = (options = {}) => useMutation(toggleUserCheering, options)

/**
 * Render button for friendship
 *
 * @param {Object} user
 * @param {Object} authedUser
 * @return {null|*}
 */
const renderFriendshipButtonUserCard = (user, authedUser) => {
    if (!user?.sportnectionStatus?.data && !user?.friendship?.data) {
        return null
    }

    if (user.sportnectionStatus?.data?.friendship_state === FRIENDSHIP_STATE_ACCEPTED) {
        return <MessengerButton user={user} />
    }

    // Due to limited time and bad architecture solution of `renderFriendshipButtons`
    // I simulated state object and used existing solution.
    return (
        renderFriendshipButtons({
            state: {
                friendshipState: user?.sportnectionStatus?.data?.friendship_state || user?.friendship?.data?.friendship_state,
                friendshipType: user?.sportnectionStatus?.data?.friendship_type || user?.friendship?.data?.friendship_type,
                user,
                authedUser,
            },
            setFriendshipLoader: () => {},
        })
    )
}

export const UserCardBox = ({
    user,
    cardContent,
    authedUser,
}) => {
    const { t } = useTranslation()
    const [isCheering, setIsCheering] = useState(user.cheers?.data?.some(cheeringUser => cheeringUser.id === authedUser.id))

    const [toggleCheeringToUser] = useToggleUserCheer({
        onMutate: variables => {
            setIsCheering(variables.isCheering)

            // Rollback that reverts cheering in case of API error
            return () => setIsCheering(!variables.isCheering)
        },
        // Call rollback on error
        onError: (_error, _vars, rollback) => rollback(),
    })

    useEffect(() => {
        setIsCheering(user.cheers?.data?.some(cheeringUser => cheeringUser.id === authedUser.id))
    }, [user.cheers])

    return (
        <StyledUserBox
            className="u-box d-inline-flex flex-column align-items-center justify-content-between flex-shrink-0 w-100 h-100">
            <Link to={`/users/${user.slug}`} className="d-flex flex-column align-items-center">
                <StyledUserAvatar
                    src={user.avatar}
                    size="avatar-md"
                    ratio={2}
                    className="rounded-circle"
                />
                <span className="font-size-lg mt-3 font-weight-bolder text-center text-color-gray-dark-1">
                    {user.name} {user.surname}
                </span>
            </Link>
            <div>
                {cardContent?.(user)}
                <div className="d-flex">
                    <Button
                        className={classnames({
                            'u-circle no-focus-outline': true,
                            'u-circle--active': isCheering,
                        })}
                        onClick={() => toggleCheeringToUser({
                            userId: user.id,
                            isCheering: !isCheering,
                        })}
                    >
                        <div className="u-circle__icon">
                            {isCheering &&
                                <SnIcon className="sn-cheer-circle-fill sn-lg text-color-brand"/>
                            }
                            {!isCheering &&
                                <SnIcon
                                    isHoverable={true}
                                    className="sn-lg text-color-brand"
                                    notHoverClassName="sn-cheer-circle-empty"
                                    hoverClassName="sn-cheer-circle-fill"
                                />
                            }
                        </div>
                        {isCheering ? t('iCheer') : t('cheer')}
                    </Button>
                    {renderFriendshipButtonUserCard(user, authedUser)}
                </div>
            </div>
        </StyledUserBox>
    )
}
